import {QueryStatus, QuerySubState} from '@reduxjs/toolkit/query'
import {
  getAccommodationPageHotelId,
  getShouldDisplayAlternativeOffers
} from 'modules/accommodation/selectors'
import {SearchOffer} from 'modules/sapiSearch/slice'
import {getTopOffersParameters} from 'modules/searchApi/selectors'
import {createSelector} from 'reselect'
import {RootState} from 'store'

import isBofhOffer from '@vio/core/src/offer/business/isBofhOffer'
import {offerCheckApi} from '@vio/core/src/offer/services/offerCheckApi'
import {OffersState, searchApi} from '@vio/core/src/sapi/services/searchApi'

type OfferCheckOptions = {offerId?: string; isProactiveCheck?: boolean}

const filterOutVioOffers = (offers: SearchOffer[]) =>
  offers.filter(offer => !isBofhOffer(offer))

export const getAlternativeOffersQueryParams = createSelector(
  [getTopOffersParameters],
  topOffersParameters => {
    return {
      ...topOffersParameters,
      getAllOffers: true,
      shouldUpdateOfferEntities: false
    }
  }
)

const getOffersQuery = (state: RootState) => {
  const hotelId = getAccommodationPageHotelId(state)
  const params = getAlternativeOffersQueryParams(state, hotelId)

  return searchApi.endpoints.getOffers.select(params)(state)
}

const getOffersQueryData = createSelector(
  [getOffersQuery],
  ({status, data}) => {
    if (status === QueryStatus.fulfilled) {
      return data
    }
  }
)

const getOffersEntity = createSelector(
  [getOffersQueryData],
  data => data?.offerEntity
)

/**
 * Gets SAPI.offers endpoint request state (not rtkq query status)
 * @returns (SAPI)OffersState
 */
const getAlternativeOffersLoadingState = createSelector(
  [getOffersQueryData],
  data => data?.status
)

export const getOffersEntityWithoutVioOffers = createSelector(
  [getOffersEntity],
  offersEntity => ({
    ...offersEntity,
    offers: filterOutVioOffers(offersEntity?.offers || [])
  })
)

/**
 * Gets filtered (only non-bofh), first 3 offers
 */
export const getAlternativeOffersList = createSelector(
  [getOffersEntity],
  offersEntity => filterOutVioOffers(offersEntity?.offers || []).slice(0, 3)
)

const getOfferCheckQueries = (state: RootState) =>
  state[offerCheckApi.reducerPath]?.queries as
    | Record<string, QuerySubState<undefined>>
    | undefined

/**
 * Checks if any user-initiated (non-proactive) offer check is in progress.
 * Proactive checks are excluded from showing the loading overlay.
 */
export const getIsAnyOfferCheckCallStarted = createSelector(
  [getOfferCheckQueries],
  offerCheckQueries => {
    const queries = offerCheckQueries ?? {}
    return Object.values(queries).some(query => {
      if (
        !query ||
        ![QueryStatus.pending, QueryStatus.uninitialized].includes(query.status)
      ) {
        return false
      }
      const options = (
        query.originalArgs as {options?: {isProactiveCheck?: boolean}}
      )?.options
      const isProactive = options?.isProactiveCheck === true
      return !isProactive
    })
  }
)

/**
 * Returns offer IDs whose proactive check failed.
 * Only includes rejected queries that had isProactiveCheck === true (excludes user-initiated checks).
 */
export const getProactiveCheckFailedOfferIds = createSelector(
  [getOfferCheckQueries],
  offerCheckQueries => {
    const failed: Record<string, boolean> = {}
    for (const query of Object.values(offerCheckQueries ?? {})) {
      const options = (query?.originalArgs as {options?: OfferCheckOptions})
        ?.options
      if (
        query?.status === 'rejected' &&
        options?.isProactiveCheck === true &&
        options?.offerId
      ) {
        failed[options.offerId] = true
      }
    }
    return failed
  }
)

export const getIsOfferCheckLoadingOverlayVisible = createSelector(
  [getIsAnyOfferCheckCallStarted, getAlternativeOffersLoadingState],
  (isAnyOfferCheckCallPending, alternativeOffersLoadingState) =>
    isAnyOfferCheckCallPending ||
    alternativeOffersLoadingState === OffersState.Pending
)

export const getIsAlternativeOffersOverlayOpen = createSelector(
  [getShouldDisplayAlternativeOffers, getAlternativeOffersList],
  (shouldDisplayAlternativeOffers, alternativeOffersList) =>
    shouldDisplayAlternativeOffers && alternativeOffersList.length > 0
)
